import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";
import { VITE_UTILITIES_BASE_URL } from "../../../env";
import { Utility } from "../../../types";
import { CamelCasedProperties } from "type-fest";
import { ClientOutput } from "../client";
import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "../../postgrestApi";
import request from "../../request";
import moment from "moment";
import { throwBackpackSuperagentResponseError } from "../errors";

export type BackpackMeter = Omit<
  CamelCasedProperties<ClientOutput["utilities"]["Meter"]>,
  "utilityType"
> & { utilityType: Utility };

const QUERY_KEY = "backpack-meters";

const meters = {
  useQuery,
  useQueryAll,
  mutations: {
    usePost,
    usePut,
    useDelete,
  },
} as const;

export default meters;

// GET

// SINGLE

// endpoint requires {feature: "Utilities", access: "read"}
const getMeter = (
  siteId: number,
  meterId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .get(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/meters/${meterId}`)
    .set(checkFeatureAccessHeader)
    .then((res) =>
      formatMeter(recursiveCamelCaseCipher(res.body) as BackpackMeter, {
        direction: "inbound",
      })
    );

function useQuery(siteId: number, meterId: number) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getMeter(siteId, meterId, checkFeatureAccessHeader),
    {
      queryKey: [QUERY_KEY, siteId, meterId],
    }
  );
}

// ALL

// endpoint requires {feature: "Utilities", access: "read"}
const getAllMeters = (
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .get(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/meters`)
    .set(checkFeatureAccessHeader)
    .then((res) => recursiveCamelCaseCipher(res.body) as BackpackMeter[])
    .then((m) =>
      m
        .map((meter) => formatMeter(meter, { direction: "inbound" }))
        .sort((a, b) => a.id - b.id)
    );

function useQueryAll(siteId: number) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getAllMeters(siteId, checkFeatureAccessHeader),
    {
      queryKey: [QUERY_KEY, siteId],
    }
  );
}

// POST

type PostUtilityMeterRequest = Partial<Omit<BackpackMeter, "id" | "siteId">>;

interface PostUtilityMeterResponse {
  id: number;
}

// endpoint requires {feature: "Utilities", access: "edit"}
const postUtilityMeter = (
  siteId: number,
  meter: PostUtilityMeterRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<PostUtilityMeterResponse> =>
  request
    .post(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/meters`)
    .set("Content-Type", "application/json")
    .set(checkFeatureAccessHeader)
    .send(
      recursiveSnakeCaseCipher(formatMeter(meter), {
        convertUtilityValues: true,
      })
    )
    .then((res) => {
      if (res.status !== 200) {
        throw new Error(`Unable to create meter for site: ${siteId}`);
      }
      return recursiveCamelCaseCipher(res.body);
    })
    .catch(throwBackpackSuperagentResponseError);

function usePost(siteId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (meter: PostUtilityMeterRequest, checkFeatureAccessHeader) =>
      postUtilityMeter(siteId, meter, checkFeatureAccessHeader),
    {
      onSuccess: async () =>
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY, siteId],
        }),
      onError: (e, { meterName }) => {
        throw new Error(
          `Unable to create meter "${meterName}"
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}

// PUT

type PutUtilityMeterRequest = Omit<BackpackMeter, "id">;

// endpoint requires {feature: "Utilities", access: "edit"}
const putUtilityMeter = (
  siteId: number,
  meterId: number,
  meter: PutUtilityMeterRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .put(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/meters/${meterId}`)
    .set(checkFeatureAccessHeader)
    .send(
      recursiveSnakeCaseCipher(formatMeter(meter), {
        convertUtilityValues: true,
      })
    )
    .then(() => {
      return;
    })
    .catch(throwBackpackSuperagentResponseError);

function usePut(siteId: number, meterId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (meter: PutUtilityMeterRequest, checkFeatureAccessHeader) =>
      putUtilityMeter(siteId, meterId, meter, checkFeatureAccessHeader),
    {
      onSuccess: async () =>
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY, siteId],
        }),
    }
  );
}

// DELETE

// endpoint requires {feature: "Utilities", access: "edit"}
const deleteUtilityMeter = (
  siteId: number,
  meterId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .delete(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/meters/${meterId}`)
    .set(checkFeatureAccessHeader)
    .then(() => {
      return;
    })
    .catch(throwBackpackSuperagentResponseError);

function useDelete(siteId: number, meterId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (_: void, checkFeatureAccessHeader) =>
      deleteUtilityMeter(siteId, meterId, checkFeatureAccessHeader),
    {
      onSuccess: async () =>
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY, siteId],
        }),
    }
  );
}

// Enums
export const billsSourceOptions = [
  "automated_bill_upload",
  "automated_credentials",
  "manual_upload",
  "utility_provider_aggregate",
] as const satisfies Readonly<Array<BackpackMeter["billsSource"]>>;

export const energySourceOptions = [
  "electric_driven",
  "absorption",
  "engine_driven",
  "other",
] as const;

export const heatingSubTypeOptions = ["steam", "hot_water"] as const;

export const expectedBillFrequencyOptions = [
  "monthly",
  "quarterly",
  "annual",
  "other",
] as const;

export const waterUsageOptions = [
  "potable",
  "reclaimed",
  "well",
  "other",
] as const;

export const waterUsageTypeOptions = ["indoor", "outdoor", "mixed"] as const;

export const wasteUsageOptions = [
  "trash",
  "mixed_recyclables",
  "compostable",
] as const;

function formatMeter<T extends Partial<BackpackMeter>>(
  meter: T,
  { direction }: { direction: "inbound" | "outbound" } = {
    direction: "outbound",
  }
) {
  // since date is type-in, harder to manage with format/parse in form
  // easier to just convert here and use a consistent format
  const inboundDateFormatter = (date?: string | null) =>
    moment(date, "YYYY-MM-DD").format("MM/DD/YYYY");
  const outboundDateFormatter = (date?: string | null) =>
    moment(date, "MM/DD/YYYY", true).format("YYYY-MM-DD");

  const formatter =
    direction === "inbound" ? inboundDateFormatter : outboundDateFormatter;

  if (meter.inactiveDate) {
    return {
      ...meter,
      inactiveDate: formatter(meter.inactiveDate),
    };
  }
  return meter;
}
